import { Share, PlusSquare, BellRing } from 'lucide-react'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { useInstall } from '@/shared/hooks/use-install'
import { showToast } from '@/shared/ui/toast'

/**
 * Install how-to sheet — the fallback when no native prompt exists (iOS,
 * Firefox). On iOS this is the ONLY path, and it unlocks push too, so the
 * sheet says so. Callers open it via `onOpenInstall()` from useInstallAction.
 */
export function InstallSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { ios } = useInstall()
  return (
    <BottomSheet open={open} onClose={onClose} title="Instalar uPadelMatch">
      <div className="flex flex-col gap-3 pb-2">
        {ios ? (
          <>
            <Step icon={<Share size={17} />} n={1}>
              Toca <strong>Compartir</strong> en la barra de Safari.
            </Step>
            <Step icon={<PlusSquare size={17} />} n={2}>
              Elige <strong>Añadir a pantalla de inicio</strong>.
            </Step>
            <Step icon={<BellRing size={17} />} n={3}>
              Abre la app instalada — ahí también funcionan los avisos.
            </Step>
          </>
        ) : (
          <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
            Abre el menú de tu navegador y elige{' '}
            <strong>Instalar aplicación</strong> (o "Añadir a pantalla de inicio").
          </p>
        )}
      </div>
    </BottomSheet>
  )
}

function Step({ icon, n, children }: { icon: React.ReactNode; n: number; children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-3 text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
      <span className="shrink-0 mt-0.5 text-[color:var(--accent)]">{icon}</span>
      <span>
        <span className="font-semibold text-[color:var(--fg)]">{n}.</span> {children}
      </span>
    </p>
  )
}

/**
 * One-call install action: native prompt where available, the how-to sheet
 * elsewhere. Returns the click handler + sheet state for the caller to render.
 */
export function useInstallAction(setSheetOpen: (open: boolean) => void) {
  const install = useInstall()
  return async function onInstallClick() {
    if (install.canPrompt) {
      const accepted = await install.promptInstall()
      if (accepted) showToast('¡Instalada! Búscala en tu pantalla de inicio')
    } else {
      setSheetOpen(true)
    }
  }
}
